import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import CircularProgress from '@mui/material/CircularProgress';
import ClickAwayListener from '@mui/material/ClickAwayListener';
import Grow from '@mui/material/Grow';
import IconButton from '@mui/material/IconButton';
import ListSubheader from '@mui/material/ListSubheader';
import Popper from '@mui/material/Popper';
import Stack from '@mui/material/Stack';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import { alpha, useTheme } from '@mui/material/styles';
import { useTranslation } from '@tyro/i18n';
import {
  ArrowLeftIcon,
  ChevronDownIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  MoveGroupIcon,
} from '@tyro/icons';
import { Link } from '@tyro/router/components';
import {
  type ReactNode,
  useCallback,
  useDeferredValue,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useDisclosure } from '../../hooks/use-disclosure';
import { RouterLink } from '../router-link';
import { SearchInput } from '../search-input';
import type { DataCollectionItemOption } from './types';
import { VirtualizedList } from './virtualized-list';

export interface DataCollectionItemContainerProps<
  Item extends DataCollectionItemOption,
> {
  parentPath: {
    href: string;
    name: string;
  };
  listNameOverride?: string;
  currentGroupItemId: Item['id'];
  groupItems: Item[];
  getItemLink: (item: Item) => string;
  optionTextKey?: string;
  getOptionText?: (item: Item) => string;
  getRenderOption?: (item: Item) => ReactNode;
  children?: ReactNode;
}

export function DataCollectionItemContainer<
  Item extends DataCollectionItemOption,
>({
  parentPath,
  listNameOverride,
  currentGroupItemId,
  groupItems,
  getItemLink,
  optionTextKey,
  getOptionText: customGetOptionText,
  getRenderOption,
  children,
}: DataCollectionItemContainerProps<Item>) {
  const id = useId();
  const anchorEl = useRef<HTMLButtonElement | null>(null);
  const headerRef = useRef<HTMLDivElement | null>(null);
  const [containerRef, setContainerRef] = useState<HTMLDivElement | null>(null);
  const [searchString, setSearchString] = useState('');
  const deferredSearchValue = useDeferredValue(searchString);
  const [currentItemId, setCurrentItemId] = useState<Item['id'] | undefined>(
    currentGroupItemId,
  );
  const { t } = useTranslation(['navigation', 'common']);
  const { palette } = useTheme();
  const { isOpen, onOpen, onClose } = useDisclosure();

  const getOptionText = useMemo(() => {
    if (
      process.env.NODE_ENV === 'development' &&
      !optionTextKey &&
      !customGetOptionText
    ) {
      console.error(
        'You must pass either optionTextKey or getOptionText to ListNavigator',
      );
    }

    return optionTextKey
      ? (option: DataCollectionItemOption) => option[optionTextKey] as string
      : customGetOptionText;
  }, [optionTextKey, customGetOptionText]);

  const listDataWithRenderedOptions = useMemo(
    () =>
      groupItems.map((item) => ({
        ...item,
        to: getItemLink(item) ?? '',
        renderedOption: getRenderOption?.(item) ?? getOptionText?.(item) ?? '',
      })),
    [groupItems, getItemLink, getRenderOption, getOptionText],
  );

  const filteredItems = useMemo(() => {
    const searchStringAsLowercase = searchString.toLowerCase();
    return listDataWithRenderedOptions.filter((item) => {
      const optionText = getOptionText?.(item) ?? '';
      return optionText.toLowerCase().includes(searchStringAsLowercase);
    });
  }, [listDataWithRenderedOptions, getOptionText, deferredSearchValue]);

  const handleSelectItem = useCallback((item: Item['id']) => {
    setCurrentItemId(item);
    onClose();
  }, []);

  useEffect(() => {
    if (isOpen) {
      setSearchString('');
    }
  }, [isOpen]);

  const currentItemIndex = useMemo(
    () =>
      listDataWithRenderedOptions.findIndex(
        (item) => item.id === currentItemId,
      ),
    [listDataWithRenderedOptions, currentItemId],
  );
  const currentItem =
    currentItemIndex !== -1
      ? listDataWithRenderedOptions[currentItemIndex]
      : undefined;
  const previousItem = useMemo(() => {
    if (currentItemIndex === 0) {
      return undefined;
    }
    return listDataWithRenderedOptions[currentItemIndex - 1];
  }, [listDataWithRenderedOptions, currentItemIndex]);

  const nextItem = useMemo(() => {
    if (currentItemIndex === listDataWithRenderedOptions.length - 1) {
      return undefined;
    }
    return listDataWithRenderedOptions[currentItemIndex + 1];
  }, [listDataWithRenderedOptions, currentItemIndex]);

  return (
    <Card
      variant="soft"
      sx={{
        display: 'flex',
        flex: 1,
        flexDirection: 'column',
        padding: 2,
        overflow: 'visible',
      }}
    >
      <Stack
        flexDirection="row"
        alignItems="center"
        justifyContent="space-between"
        sx={{
          backgroundColor: alpha(palette.primary.lighter, 0.3),
          borderRadius: 1.5,
          p: 0.5,
        }}
      >
        <Button
          component={Link}
          startIcon={<ChevronLeftIcon sx={{ width: 24, height: 24 }} />}
          color="primary"
          variant="text"
          sx={{
            '.MuiButton-startIcon': {
              marginRight: 0,
            },
          }}
          to={previousItem?.to ?? ''}
          onClick={() => setCurrentItemId(previousItem?.id)}
          disabled={!previousItem}
        >
          {t('common:actions.previous')}
        </Button>

        <Stack direction="row" alignItems="center">
          <Tooltip title={t('common:backToPage', { page: parentPath.name })}>
            <IconButton
              component={Link}
              to={parentPath.href}
              color="primary"
              sx={{
                'svg g': {
                  strokeWidth: 2,
                },
              }}
            >
              <MoveGroupIcon sx={{ transform: 'rotate(-90deg)' }} />
            </IconButton>
          </Tooltip>
          <Button
            ref={anchorEl}
            id={id}
            variant="soft"
            aria-expanded={isOpen ? 'true' : undefined}
            aria-controls={isOpen ? `${id}-menu` : undefined}
            aria-haspopup="true"
            endIcon={<ChevronDownIcon />}
            onClick={onOpen}
          >
            {!currentItem && <CircularProgress size={16} sx={{ mr: 1 }} />}

            {currentItem ? getOptionText?.(currentItem) : ''}
          </Button>
          {/* biome-ignore lint/a11y/useValidAriaRole: Need to remove default role */}
          <Popper
            open={isOpen}
            anchorEl={anchorEl.current}
            role={undefined}
            placement="bottom"
            transition
            disablePortal
            sx={{
              zIndex: 'modal',
            }}
          >
            {({ TransitionProps }) => (
              <ClickAwayListener onClickAway={onClose}>
                <Grow
                  {...TransitionProps}
                  timeout={350}
                  style={{
                    transformOrigin: 'center top',
                  }}
                >
                  <Stack
                    ref={setContainerRef}
                    sx={({ customShadows, palette }) => ({
                      minHeight: 172,
                      maxHeight: 'min(calc(100vh - 96px), 400px)',
                      position: 'relative',
                      overflow: 'auto',
                      mt: 1,
                      borderRadius: 2,
                      boxShadow: customShadows.dropdown,
                      backgroundColor: palette.background.paper,
                    })}
                  >
                    <ListSubheader
                      ref={headerRef}
                      component={Stack}
                      sx={{
                        pt: 1,
                        px: 1,
                        backgroundColor: 'rgba(255, 255, 255, 0.8)',
                        backdropFilter: 'blur(20px)',
                      }}
                    >
                      <Stack
                        flexDirection="row"
                        alignItems="center"
                        justifyContent="space-between"
                        gap={4}
                        pb={0.5}
                      >
                        {parentPath?.href && (
                          <Button
                            variant="text"
                            size="small"
                            component={RouterLink}
                            to={parentPath?.href}
                            startIcon={<ArrowLeftIcon />}
                            sx={{
                              '.MuiButton-startIcon': {
                                marginRight: 0.25,
                              },
                            }}
                          >
                            {t('common:profileNavigation.breadcrumbPage', {
                              name: parentPath.name,
                            })}
                          </Button>
                        )}
                        <Stack flexDirection="row" gap={0.5}>
                          <Typography
                            variant="body2"
                            color="text.secondary"
                            fontWeight="bold"
                          >
                            {listNameOverride ?? parentPath.name}
                          </Typography>
                          <Typography variant="body2">
                            ({groupItems.length})
                          </Typography>
                        </Stack>
                      </Stack>
                      <SearchInput
                        value={searchString}
                        onChange={(event) => {
                          setSearchString(event.target.value);
                        }}
                      />
                    </ListSubheader>
                    {filteredItems.length === 0 ? (
                      <Stack
                        sx={{
                          width: '100%',
                          justifyContent: 'center',
                          alignItems: 'center',
                          minHeight: 80,
                        }}
                      >
                        <Typography
                          variant="body1"
                          component="span"
                          color="text.secondary"
                        >
                          {t('common:noResultsFound')}
                        </Typography>
                      </Stack>
                    ) : (
                      containerRef &&
                      currentItem && (
                        <VirtualizedList
                          containerRef={containerRef}
                          headerHeight={headerRef.current?.clientHeight ?? 0}
                          filteredItems={filteredItems}
                          currentItemId={currentItem.id}
                          onSelectItem={handleSelectItem}
                        />
                      )
                    )}
                  </Stack>
                </Grow>
              </ClickAwayListener>
            )}
          </Popper>
        </Stack>

        <Button
          component={Link}
          endIcon={<ChevronRightIcon sx={{ width: 24, height: 24 }} />}
          color="primary"
          variant="text"
          sx={{
            '.MuiButton-endIcon': {
              marginLeft: 0,
            },
          }}
          to={nextItem?.to ?? ''}
          disabled={!nextItem}
          onClick={() => setCurrentItemId(nextItem?.id)}
        >
          {t('common:actions.next')}
        </Button>
      </Stack>
      {children}
    </Card>
  );
}
